import React, { useEffect, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  TextField,
  Divider,
} from "@mui/material";
import * as pdfjsLib from "pdfjs-dist";
import qrImg from "../../images/qr.jpeg";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import { BASE_URL } from "../../constants";
import TuneIcon from "@mui/icons-material/Tune";
import SaveIcon from "@mui/icons-material/Save";

// Serve the worker from this app so PDF parsing does not depend on a runtime CDN.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

const sectionStyle = {
  background: "#fff",
  padding: 12,
  borderRadius: 8,
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  marginBottom: 16,
};

const grid2 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
};

const inputStyle = {
  width: "100%",
  height: 34,
  padding: "4px 6px",
  borderRadius: 6,
  border: "1px solid #ccc",
  fontSize: 13,
};

const labelStyle = {
  fontSize: 11,
  color: "#555",
  marginBottom: 2,
};

export default function PathologyReportLayoutGuide() {
  /* ================= PDF ================= */
  const [viewport, setViewport] = useState(null);
  const [zoom, setZoom] = useState(1);

  /* ================= HEADER ================= */
  const [headerX, setHeaderX] = useState(33);
  const [headerY, setHeaderY] = useState(140);
  const [headerWidth, setHeaderWidth] = useState(555);
  const [headerHeight, setHeaderHeight] = useState(96);
  const [colWidths, setColWidths] = useState([247, 200, 108]);

  /* ================= CONTENT ================= */
  const [contentX, setContentX] = useState(30);
  const [contentY, setContentY] = useState(247);
  const [contentWidth, setContentWidth] = useState(556);
  const [contentHeight, setContentHeight] = useState(484);

  /* ================= FOOTER SIGNATURES ================= */
  const SIGN_W = 94;
  const SIGN_H = 60;

  const [techSignX, setTechSignX] = useState(383);
  const [techSignY, setTechSignY] = useState(744); // set after pdf load

  const [docSignX, setDocSignX] = useState(492);
  const [docSignY, setDocSignY] = useState(744); // set after pdf load

  /* ================= DRAG ================= */
  const [dragging, setDragging] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const [doctorSignCount, setDoctorSignCount] = useState(1);
  const [docSignPositions, setDocSignPositions] = useState([
    { x: docSignX, y: docSignY },
    { x: docSignX - 120, y: docSignY },
    { x: docSignX - 240, y: docSignY },
  ]);

  const loadPdfFromUrl = async (pdfUrl) => {
    try {
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);

      const vp = page.getViewport({ scale: 1 });
      setViewport(vp);

      const canvas = document.getElementById("pdf-canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = vp.width;
      canvas.height = vp.height;

      await page.render({
        canvasContext: ctx,
        viewport: vp,
      }).promise;
    } catch (err) {
      console.error("❌ Failed to load PDF from URL:", err);
    }
  };

  const applyLayoutToState = (layout) => {
    if (!layout) return;

    /* ================= HEADER ================= */
    if (layout.header) {
      setHeaderX(layout.header.position?.x ?? headerX);
      setHeaderY(layout.header.position?.y ?? headerY);
      setHeaderWidth(layout.header.size?.width ?? headerWidth);

      const cols = layout.header.columns;
      if (cols) {
        setColWidths([
          cols.patient?.width ?? colWidths[0],
          cols.uhid?.width ?? colWidths[1],
          cols.qr?.width ?? colWidths[2],
        ]);
      }
    }

    /* ================= CONTENT ================= */
    if (layout.content) {
      setContentX(layout.content.position?.x ?? contentX);
      setContentY(layout.content.position?.y ?? contentY);
      setContentWidth(layout.content.size?.width ?? contentWidth);
      setContentHeight(layout.content.size?.height ?? contentHeight);
    }

    /* ================= FOOTER ================= */
    if (layout.footer) {
      const tech = layout.footer.technicianSignature;

      if (tech) {
        setTechSignX(tech.position?.x ?? techSignX);
        setTechSignY(tech.position?.y ?? techSignY);
      }

      /* 🔥 NEW: MULTI DOCTOR SUPPORT */
      if (layout.footer.doctorSignatures) {
        const docData = layout.footer.doctorSignatures;

        const count = docData.count || 1;
        const positions = docData.positions || [];

        setDoctorSignCount(count);

        const mappedPositions = positions.map((p, idx) => ({
          x: p.position?.x ?? docSignX + idx * 120,
          y: p.position?.y ?? docSignY,
        }));

        // Ensure always 3 slots (UI safe)
        while (mappedPositions.length < 3) {
          mappedPositions.push({
            x: docSignX + mappedPositions.length * 120,
            y: docSignY,
          });
        }

        setDocSignPositions(mappedPositions);
      } else if (layout.footer.doctorSignature) {
        /* ⚠️ BACKWARD COMPATIBILITY (OLD DATA) */
        const doc = layout.footer.doctorSignature;

        setDoctorSignCount(1);

        setDocSignPositions([
          {
            x: doc.position?.x ?? docSignX,
            y: doc.position?.y ?? docSignY,
          },
          { x: docSignX + 120, y: docSignY },
          { x: docSignX + 240, y: docSignY },
        ]);
      }
    }
  };

  const fetchTemplatePdf = async () => {
    try {
      const res = await fetch(`${BASE_URL}api/org/report-template`, {
        headers: {
        },
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to load template PDF");
      }
      console.log(data);
      const pdfUrl = data.reportTemplate;

      if (pdfUrl) {
        await loadPdfFromUrl(pdfUrl);
      }
    } catch (err) {
      console.error("❌ Error loading template PDF:", err);
    }
  };

  const fetchTemplatePdfLayout = async () => {
    try {
      const res = await fetch(`${BASE_URL}api/org/patho-layout`, {
        headers: {
        },
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to load layout");
      }

      if (data.layout) {
        applyLayoutToState(data.layout);
        console.log("✅ Layout applied from backend");
      }
    } catch (err) {
      console.error("❌ Error loading layout:", err);
    }
  };

  useEffect(() => {
    fetchTemplatePdf();
    fetchTemplatePdfLayout();
  }, []);

  /* ================= LOAD PDF ================= */
  const loadPdf = async (file) => {
    if (!file) return;
    const buffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 1 });
    setViewport(vp);

    const canvas = document.getElementById("pdf-canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = vp.width;
    canvas.height = vp.height;

    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    canvas.style.width = `${vp.width * zoom}px`;
    canvas.style.height = `${vp.height * zoom}px`;
  };

  /* ================= START DRAG ================= */
  const startDrag = (e, type, x, y) => {
    const rect = document
      .getElementById("canvasWrapper")
      .getBoundingClientRect();

    setOffset({
      x: e.clientX - rect.left - x * zoom,
      y: e.clientY - rect.top - y * zoom,
    });
    setDragging(type);
    e.stopPropagation();
  };

  /* ================= MOUSE MOVE ================= */
  const handleMouseMove = (e) => {
    if (!dragging || !viewport) return;

    const rect = document
      .getElementById("canvasWrapper")
      .getBoundingClientRect();

    const mouseX = (e.clientX - rect.left) / zoom;
    const mouseY = (e.clientY - rect.top) / zoom;

    /* HEADER MOVE */
    if (dragging === "header") {
      setHeaderX(
        Math.max(
          0,
          Math.min(mouseX - offset.x / zoom, viewport.width - headerWidth),
        ),
      );
      setHeaderY(
        Math.max(
          0,
          Math.min(mouseY - offset.y / zoom, viewport.height - headerHeight),
        ),
      );
    }

    /* HEADER RESIZE (WIDTH + HEIGHT) */
    /* HEADER RESIZE (WIDTH ONLY) */
    if (dragging === "header-resize") {
      const newW = Math.max(300, mouseX - headerX);

      const scale = newW / headerWidth;
      setHeaderWidth(Math.round(newW));
      setColWidths(colWidths.map((w) => Math.round(w * scale)));
    }

    /* CONTENT MOVE */
    if (dragging === "content") {
      setContentX(
        Math.max(
          0,
          Math.min(mouseX - offset.x / zoom, viewport.width - contentWidth),
        ),
      );
      setContentY(
        Math.max(
          0,
          Math.min(mouseY - offset.y / zoom, viewport.height - contentHeight),
        ),
      );
    }

    /* CONTENT RESIZE */
    if (dragging === "content-resize") {
      setContentWidth(Math.max(200, mouseX - contentX));
      setContentHeight(Math.max(150, mouseY - contentY));
    }

    /* COLUMN RESIZE */
    if (dragging?.startsWith("col-")) {
      const i = Number(dragging.split("-")[1]);
      const leftEdge =
        headerX + colWidths.slice(0, i).reduce((a, b) => a + b, 0);

      const newW = mouseX - leftEdge;
      const min = 80;

      if (newW > min && colWidths[i + 1] > min) {
        const diff = newW - colWidths[i];
        const cols = [...colWidths];
        cols[i] += diff;
        cols[i + 1] -= diff;
        setColWidths(cols.map((v) => Math.round(v)));
      }
    }
    /* QR COLUMN RESIZE (LAST COLUMN) */
    if (dragging === "col-qr") {
      const min = 80;

      // Left edge of QR column
      const qrLeft = headerX + colWidths[0] + colWidths[1];

      const newQrWidth = mouseX - qrLeft;

      if (newQrWidth > min) {
        const newCols = [...colWidths];
        newCols[2] = Math.round(newQrWidth);

        setColWidths(newCols);

        // Header width = sum of all columns
        setHeaderWidth(newCols.reduce((a, b) => a + b, 0));
      }
    }

    /* TECH SIGN MOVE */
    if (dragging === "tech-sign") {
      setTechSignX(
        Math.max(
          0,
          Math.min(mouseX - offset.x / zoom, viewport.width - SIGN_W),
        ),
      );
      setTechSignY(
        Math.max(
          0,
          Math.min(mouseY - offset.y / zoom, viewport.height - SIGN_H),
        ),
      );
    }

    /* ================= MULTI DOCTOR SIGN MOVE ================= */
    if (dragging?.startsWith("doc-sign-")) {
      const index = Number(dragging.split("-")[2]);

      setDocSignPositions((prev) => {
        const updated = [...prev];

        const newX = Math.max(
          0,
          Math.min(mouseX - offset.x / zoom, viewport.width - SIGN_W),
        );

        const newY = Math.max(
          0,
          Math.min(mouseY - offset.y / zoom, viewport.height - SIGN_H),
        );

        updated[index] = { x: newX, y: newY };

        return updated;
      });
    }
  };

  const stopDrag = () => setDragging(null);

  const getHeaderColumns = () => {
    return colWidths.map((w, index) => ({
      index,
      width: w,
      percent: ((w / headerWidth) * 100).toFixed(2),
    }));
  };

  /* ================= SAVE ================= */
  const saveLayout = async () => {
    try {
      const layoutConfig = {
        header: {
          position: { x: headerX, y: headerY },
          size: { width: headerWidth, height: headerHeight },
          columns: {
            patient: { width: colWidths[0] },
            uhid: { width: colWidths[1] },
            qr: { width: colWidths[2] },
          },
        },

        content: {
          position: { x: contentX, y: contentY },
          size: { width: contentWidth, height: contentHeight },
        },

        footer: {
          technicianSignature: {
            position: { x: techSignX, y: techSignY },
            size: { width: SIGN_W, height: SIGN_H },
          },

          doctorSignatures: {
            count: doctorSignCount,
            positions: docSignPositions
              .slice(0, doctorSignCount)
              .map((pos) => ({
                position: {
                  x: pos?.x ?? 0,
                  y: pos?.y ?? 0,
                },
                size: {
                  width: SIGN_W,
                  height: SIGN_H,
                },
              })),
          },
        },
      };

      const res = await fetch(`${BASE_URL}api/org/patho-layout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(layoutConfig),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to save layout");
      }

      console.log("✅ Layout saved successfully", data.data);
      alert("Pathology layout saved successfully!");
    } catch (error) {
      console.error("❌ Save layout error:", error);
      alert("Failed to save layout");
    }
  };

  /* ================= RENDER ================= */
  return (
    // <Grid container sx={{ height: "100vh" }}>
    //   <Grid item xs={9} sx={{ p: 2, borderRight: "1px solid #ccc" }}>
    //     <div
    //       id="canvasWrapper"
    //       onMouseMove={handleMouseMove}
    //       onMouseUp={stopDrag}
    //       onMouseLeave={stopDrag}
    //       style={{
    //         position: "relative",
    //         width: viewport ? viewport.width * zoom : "auto",
    //         height: viewport ? viewport.height * zoom : "auto",
    //         margin: "20px auto",
    //         background: viewport ? "#fff" : "#eee",
    //         boxShadow: "0px 0px 12px 1px black",
    //       }}
    //     >
    //       <canvas id="pdf-canvas" />

    //       {/* ================= HEADER ================= */}
    //       {viewport && (
    //         <div
    //           onMouseDown={(e) => startDrag(e, "header", headerX, headerY)}
    //           style={{
    //             position: "absolute",
    //             left: headerX * zoom,
    //             top: headerY * zoom,
    //             width: headerWidth * zoom,
    //             height: headerHeight * zoom,
    //             display: "flex",
    //             background: "rgba(0,120,255,0.12)",
    //             border: "2px dashed #0078ff",
    //             cursor: "move",
    //           }}
    //         >
    //           {colWidths.map((w, i) => (
    //             <div
    //               key={i}
    //               style={{
    //                 width: w * zoom,
    //                 // padding: "3px 1px 3px 20px",
    //                 background:
    //                   i === 0
    //                     ? "rgba(255,255,0,0.35)"
    //                     : i === 1
    //                     ? "rgb(255 90 0 / 35%)"
    //                     : "rgba(0,200,0,0.35)",
    //                 position: "relative",
    //               }}
    //             >
    //               {i === 0 && (
    //                 <div style={{ fontSize: 10 * zoom, lineHeight: 1.4 }}>
    //                   <b style={{ fontSize: 12 * zoom }}>PATIENT NAME</b>
    //                   <div>Sex / Age : Female / 37Y 0M 16D</div>
    //                   <div>Mobile : 9999999999</div>
    //                   <div>Ref. By : Dr. Doctor Name</div>
    //                   <div>Center : 0-0-0</div>
    //                   <div>Lab ID : 00.00</div>
    //                 </div>
    //               )}

    //               {i === 1 && (
    //                 <>
    //                   <ResizeHandleIcon side="left" />
    //                   <ResizeHandleIcon side="right" />

    //                   <div
    //                     style={{
    //                       fontSize: "8.2px",
    //                       padding: "0px 0px 0px 20px",
    //                       lineHeight: "15.5px",
    //                     }}
    //                   >
    //                     <div>
    //                       <span>UHID</span>
    //                       <span
    //                         style={{
    //                           marginLeft: "57px",
    //                           fontWeight: "600",
    //                           fontSize: "10px",
    //                         }}
    //                       >
    //                         : OP01264
    //                       </span>
    //                     </div>

    //                     <div>
    //                       <span>Case ID</span>
    //                       <span
    //                         style={{
    //                           marginLeft: "46px",
    //                           fontWeight: "600",
    //                           fontSize: "8px",
    //                         }}
    //                       >
    //                         : CI01266
    //                       </span>
    //                     </div>

    //                     <div>
    //                       <span>Sample Collected</span>
    //                       <span style={{ marginLeft: "9px" }}>
    //                         : 16-Jan-26 04:00 PM
    //                       </span>
    //                     </div>

    //                     <div>
    //                       <span>Report Generated</span>
    //                       <span style={{ marginLeft: "6px" }}>
    //                         : 16-Jan-26 06:58 PM
    //                       </span>
    //                     </div>

    //                     <div
    //                       style={{
    //                         margin: "3px 0px 0px 2px",
    //                         height: "18px",
    //                         width: "164px",
    //                         background:
    //                           "repeating-linear-gradient(90deg,#000 0,#000 2px,#fff 2px,#fff 4px)",
    //                       }}
    //                     />
    //                   </div>
    //                 </>
    //               )}

    //               {i === 2 && (
    //                 <>
    //                   <img
    //                     src={qrImg}
    //                     style={{
    //                       margin: "15px 0px 0px 21px",
    //                       width: "72px",
    //                       height: "66px",
    //                     }}
    //                   />

    //                   {/* QR RIGHT RESIZE HANDLE */}
    //                   <div
    //                     onMouseDown={(e) => {
    //                       e.stopPropagation();
    //                       setDragging("col-qr"); // 🔑 special case
    //                     }}
    //                     style={{
    //                       position: "absolute",
    //                       right: -6,
    //                       top: 0,
    //                       width: 10,
    //                       height: "100%",
    //                       cursor: "ew-resize",
    //                       color: "#000",
    //                     }}
    //                   />

    //                   {/* Visual icon */}
    //                   <MultipleStopIcon
    //                     sx={{
    //                       position: "absolute",
    //                       top: "50%",
    //                       right: -9,
    //                       fontSize: 18,
    //                       color: "#000",
    //                       //   opacity: 0.7,
    //                       pointerEvents: "none",
    //                     }}
    //                   />
    //                 </>
    //               )}

    //               {i < colWidths.length - 1 && (
    //                 <div
    //                   onMouseDown={(e) => {
    //                     e.stopPropagation();
    //                     setDragging(`col-${i}`);
    //                   }}
    //                   style={{
    //                     position: "absolute",
    //                     right: -4,
    //                     top: 0,
    //                     width: 8,
    //                     height: "100%",
    //                     cursor: "ew-resize",
    //                   }}
    //                 />
    //               )}
    //             </div>
    //           ))}

    //           {/* HEADER RESIZE HANDLE */}
    //           <div
    //             onMouseDown={(e) => {
    //               e.stopPropagation();
    //               setDragging("header-resize");
    //             }}
    //             style={{
    //               position: "absolute",
    //               right: -8,
    //               bottom: -8,
    //               width: 16,
    //               height: 16,
    //               background: "#0078ff",
    //               cursor: "nwse-resize",
    //             }}
    //           />
    //         </div>
    //       )}

    //       {/* ================= CONTENT ================= */}
    //       {viewport && (
    //         <div
    //           onMouseDown={(e) => startDrag(e, "content", contentX, contentY)}
    //           style={{
    //             position: "absolute",
    //             left: contentX * zoom,
    //             top: contentY * zoom,
    //             width: contentWidth * zoom,
    //             height: contentHeight * zoom,
    //             background: "rgb(233 0 255 / 18%)",
    //             border: "2px dashed #db00ff",
    //             cursor: "move",
    //           }}
    //         >
    //           <div style={{ padding: 8 * zoom, fontSize: 10 * zoom }}>
    //             <div
    //               style={{
    //                 display: "grid",
    //                 gridTemplateColumns: "2fr 1fr 1fr 1fr",
    //                 fontWeight: 700,
    //                 borderBottom: "1px solid #999",
    //                 paddingBottom: 4 * zoom,
    //                 marginBottom: 6 * zoom,
    //               }}
    //             >
    //               <div>INVESTIGATIONS</div>
    //               <div>RESULTS</div>
    //               <div>UNIT</div>
    //               <div>BIO REF.</div>
    //             </div>

    //             {[
    //               ["HIV", "Negative", "", "Screening"],
    //               ["HBsAG", "Negative", "", "Screening"],
    //               ["HCV", "Negative", "", "Screening"],
    //               ["TSH", "2.31", "µIU/mL", "0.4 - 4"],
    //               ["RBS", "87", "mg/dL", "75 - 140"],
    //               ["Creatinine", "0.78", "mg/dL", "0.5 - 1.1"],
    //             ].map((row, idx) => (
    //               <div
    //                 key={idx}
    //                 style={{
    //                   display: "grid",
    //                   gridTemplateColumns: "2fr 1fr 1fr 1fr",
    //                   padding: `${2 * zoom}px 0`,
    //                   borderBottom: "1px dashed #ccc",
    //                 }}
    //               >
    //                 <div>{row[0]}</div>
    //                 <div>{row[1]}</div>
    //                 <div>{row[2]}</div>
    //                 <div>{row[3]}</div>
    //               </div>
    //             ))}

    //             <div
    //               style={{
    //                 marginTop: 6 * zoom,
    //                 fontStyle: "italic",
    //                 textAlign: "center",
    //                 color: "#666",
    //               }}
    //             >
    //               *** End of report ***
    //             </div>
    //           </div>

    //           {/* CONTENT RESIZE HANDLE */}
    //           <div
    //             onMouseDown={(e) => {
    //               e.stopPropagation();
    //               setDragging("content-resize");
    //             }}
    //             style={{
    //               position: "absolute",
    //               right: -8,
    //               bottom: -8,
    //               width: 16,
    //               height: 16,
    //               background: "rgb(219, 0, 255)",
    //               cursor: "nwse-resize",
    //             }}
    //           />
    //         </div>
    //       )}

    //       {/* ================= TECHNICIAN SIGNATURE ================= */}
    //       {viewport && (
    //         <div
    //           onMouseDown={(e) =>
    //             startDrag(e, "tech-sign", techSignX, techSignY)
    //           }
    //           style={{
    //             position: "absolute",
    //             left: techSignX,
    //             top: techSignY,
    //             width: SIGN_W,
    //             height: SIGN_H,
    //             background: "rgba(0,0,0,0.08)",
    //             border: "2px dashed #555",
    //             cursor: "move",
    //             display: "flex",
    //             alignItems: "end",
    //             fontSize: "8px",
    //             fontWeight: 600,
    //           }}
    //         >
    //           <div>
    //             {/* <div
    //               style={{ height: 22 * zoom, borderBottom: "1px solid #000" }}
    //             /> */}
    //             <div>lab tech</div>
    //             <div>line2</div>
    //             <div>line3</div>
    //             <div>line4</div>
    //           </div>
    //         </div>
    //       )}

    //       {/* ================= DOCTOR SIGNATURE ================= */}
    //       {viewport && (
    //         <div
    //           onMouseDown={(e) => startDrag(e, "doc-sign", docSignX, docSignY)}
    //           style={{
    //             position: "absolute",
    //             left: docSignX,
    //             top: docSignY,
    //             width: SIGN_W,
    //             height: SIGN_H,
    //             background: "rgba(0,0,0,0.08)",
    //             border: "2px dashed #555",
    //             cursor: "move",
    //             display: "flex",
    //             alignItems: "end",
    //             fontSize: "7px",
    //             fontWeight: 600,
    //           }}
    //         >
    //           <div>
    //             {/* <div
    //               style={{ height: 22 * zoom, borderBottom: "1px solid #000" }}
    //             /> */}
    //             {/* <div>Consultant Pathologist</div> */}
    //             <div>doctor</div>
    //             <div>line2</div>
    //             <div>line3</div>
    //             <div>line4</div>
    //           </div>
    //         </div>
    //       )}
    //     </div>
    //   </Grid>

    //   {/* RIGHT PANEL */}
    //   {viewport && (
    //     <Grid
    //       item
    //       xs={3}
    //       sx={{ p: 2, backgroundColor: "#f7f9fc", overflowY: "auto" }}
    //     >
    //       <Typography variant="h6" gutterBottom>
    //         Layout Settings
    //       </Typography>

    //       {/* ================= HEADER ================= */}
    //       <Typography variant="subtitle2" gutterBottom>
    //         Header Box
    //       </Typography>

    //       <Grid container spacing={2}>
    //         <Grid item xs={6}>
    //           <TextField
    //             label="Header X"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={headerX}
    //             sx={{ backgroundColor: "rgb(0 120 255 / 25%)" }}
    //             onChange={(e) => setHeaderX(+e.target.value)}
    //           />
    //         </Grid>
    //         <Grid item xs={6}>
    //           <TextField
    //             label="Header Y"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={headerY}
    //             sx={{ backgroundColor: "rgb(0 120 255 / 25%)" }}
    //             onChange={(e) => setHeaderY(+e.target.value)}
    //           />
    //         </Grid>

    //         <Grid item xs={6}>
    //           <TextField
    //             label="Header Width"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={headerWidth}
    //             sx={{ backgroundColor: "rgb(0 120 255 / 25%)" }}
    //             onChange={(e) => {
    //               const newW = +e.target.value;
    //               const scale = newW / headerWidth;
    //               setHeaderWidth(newW);
    //               setColWidths(colWidths.map((w) => Math.round(w * scale)));
    //             }}
    //           />
    //         </Grid>
    //       </Grid>

    //       <Divider sx={{ my: 2 }} />

    //       {/* ================= HEADER COLUMNS ================= */}
    //       <Typography variant="subtitle2" gutterBottom>
    //         Header Columns
    //       </Typography>

    //       <Grid container spacing={2}>
    //         <Grid item xs={6}>
    //           <TextField
    //             label="Patient Width"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={colWidths[0]}
    //             sx={{ backgroundColor: "rgba(255,255,0,0.35)" }}
    //             onChange={(e) => {
    //               const cols = [...colWidths];
    //               cols[0] = +e.target.value;
    //               setColWidths(cols);
    //               setHeaderWidth(cols.reduce((a, b) => a + b, 0));
    //             }}
    //           />
    //         </Grid>

    //         <Grid item xs={6}>
    //           <TextField
    //             label="UHID Width"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={colWidths[1]}
    //             sx={{ backgroundColor: "rgb(255 90 0 / 35%)" }}
    //             onChange={(e) => {
    //               const cols = [...colWidths];
    //               cols[1] = +e.target.value;
    //               setColWidths(cols);
    //               setHeaderWidth(cols.reduce((a, b) => a + b, 0));
    //             }}
    //           />
    //         </Grid>

    //         <Grid item xs={6}>
    //           <TextField
    //             label="QR Width"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={colWidths[2]}
    //             sx={{ backgroundColor: "rgba(0,200,0,0.35)" }}
    //             onChange={(e) => {
    //               const cols = [...colWidths];
    //               cols[2] = +e.target.value;
    //               setColWidths(cols);
    //               setHeaderWidth(cols.reduce((a, b) => a + b, 0));
    //             }}
    //           />
    //         </Grid>
    //       </Grid>

    //       <Divider sx={{ my: 2 }} />

    //       {/* ================= CONTENT ================= */}
    //       <Typography variant="subtitle2" gutterBottom>
    //         Content Box
    //       </Typography>

    //       <Grid container spacing={2}>
    //         <Grid item xs={6}>
    //           <TextField
    //             label="Content X"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={contentX}
    //             sx={{ backgroundColor: "rgb(233 0 255 / 18%)" }}
    //             onChange={(e) => setContentX(+e.target.value)}
    //           />
    //         </Grid>
    //         <Grid item xs={6}>
    //           <TextField
    //             label="Content Y"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={contentY}
    //             sx={{ backgroundColor: "rgb(233 0 255 / 18%)" }}
    //             onChange={(e) => setContentY(+e.target.value)}
    //           />
    //         </Grid>

    //         <Grid item xs={6}>
    //           <TextField
    //             label="Content Width"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={contentWidth}
    //             sx={{ backgroundColor: "rgb(233 0 255 / 18%)" }}
    //             onChange={(e) => setContentWidth(+e.target.value)}
    //           />
    //         </Grid>
    //         <Grid item xs={6}>
    //           <TextField
    //             label="Content Height"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={contentHeight}
    //             sx={{ backgroundColor: "rgb(233 0 255 / 18%)" }}
    //             onChange={(e) => setContentHeight(+e.target.value)}
    //           />
    //         </Grid>
    //       </Grid>

    //       <Divider sx={{ my: 2 }} />

    //       {/* ================= FOOTER ================= */}
    //       <Typography variant="subtitle2" gutterBottom>
    //         Footer Signatures
    //       </Typography>

    //       <Typography variant="caption">Technician</Typography>
    //       <Grid container spacing={2} mb={1}>
    //         <Grid item xs={6}>
    //           <TextField
    //             label="X"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={techSignX}
    //             sx={{ backgroundColor: "#8080802e" }}
    //             onChange={(e) => setTechSignX(+e.target.value)}
    //           />
    //         </Grid>
    //         <Grid item xs={6}>
    //           <TextField
    //             label="Y"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={techSignY}
    //             sx={{ backgroundColor: "#8080802e" }}
    //             onChange={(e) => setTechSignY(+e.target.value)}
    //           />
    //         </Grid>
    //       </Grid>

    //       <Typography variant="caption">Doctor</Typography>
    //       <Grid container spacing={2}>
    //         <Grid item xs={6}>
    //           <TextField
    //             label="X"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={docSignX}
    //             sx={{ backgroundColor: "#8080802e" }}
    //             onChange={(e) => setDocSignX(+e.target.value)}
    //           />
    //         </Grid>
    //         <Grid item xs={6}>
    //           <TextField
    //             label="Y"
    //             size="small"
    //             fullWidth
    //             type="number"
    //             value={docSignY}
    //             sx={{ backgroundColor: "#8080802e" }}
    //             onChange={(e) => setDocSignY(+e.target.value)}
    //           />
    //         </Grid>
    //       </Grid>

    //       <Button
    //         variant="contained"
    //         fullWidth
    //         sx={{ mt: 2 }}
    //         onClick={saveLayout}
    //       >
    //         Save Layout
    //       </Button>
    //     </Grid>
    //   )}
    // </Grid>
    <Grid container sx={{ height: "100vh", background: "#f4f6fb" }}>
      {/* ══ LEFT: Canvas area ══ */}
      <Grid
        item
        xs={9}
        sx={{
          p: 2,
          borderRight: "1px solid #e5e9ef",
          background: "#e8ecf2",
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <div
          id="canvasWrapper"
          onMouseMove={handleMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
          style={{
            position: "relative",
            width: viewport ? viewport.width * zoom : "auto",
            height: viewport ? viewport.height * zoom : "auto",
            margin: "20px auto",
            background: viewport ? "#fff" : "#dde3ed",
            boxShadow: "0 4px 32px rgba(10,31,68,0.18)",
            borderRadius: "4px",
          }}
        >
          <canvas id="pdf-canvas" />

          {/* ── HEADER ── */}
          {viewport && (
            <div
              onMouseDown={(e) => startDrag(e, "header", headerX, headerY)}
              style={{
                position: "absolute",
                left: headerX * zoom,
                top: headerY * zoom,
                width: headerWidth * zoom,
                height: headerHeight * zoom,
                display: "flex",
                background: "rgba(42,84,166,0.08)",
                border: "2px dashed #2a54a6",
                cursor: "move",
                borderRadius: "2px",
              }}
            >
              {colWidths.map((w, i) => (
                <div
                  key={i}
                  style={{
                    width: w * zoom,
                    background:
                      i === 0
                        ? "rgba(42,84,166,0.12)"
                        : i === 1
                          ? "rgba(249,115,22,0.12)"
                          : "rgba(16,185,129,0.12)",
                    position: "relative",
                  }}
                >
                  {i === 0 && (
                    <div
                      style={{
                        fontSize: 10 * zoom,
                        lineHeight: 1.4,
                        padding: `${4 * zoom}px`,
                      }}
                    >
                      <b style={{ fontSize: 12 * zoom }}>PATIENT NAME</b>
                      <div>Sex / Age : Female / 37Y 0M 16D</div>
                      <div>Mobile : 9999999999</div>
                      <div>Ref. By : Dr. Doctor Name</div>
                      <div>Center : 0-0-0</div>
                      <div>Lab ID : 00.00</div>
                    </div>
                  )}
                  {i === 1 && (
                    <>
                      <ResizeHandleIcon side="left" />
                      <ResizeHandleIcon side="right" />
                      <div
                        style={{
                          fontSize: "8.2px",
                          padding: "0px 0px 0px 20px",
                          lineHeight: "15.5px",
                        }}
                      >
                        <div>
                          <span>UHID</span>
                          <span
                            style={{
                              marginLeft: "57px",
                              fontWeight: 600,
                              fontSize: "10px",
                            }}
                          >
                            : OP01264
                          </span>
                        </div>
                        <div>
                          <span>Case ID</span>
                          <span
                            style={{
                              marginLeft: "46px",
                              fontWeight: 600,
                              fontSize: "8px",
                            }}
                          >
                            : CI01266
                          </span>
                        </div>
                        <div>
                          <span>Sample Collected</span>
                          <span style={{ marginLeft: "9px" }}>
                            : 16-Jan-26 04:00 PM
                          </span>
                        </div>
                        <div>
                          <span>Report Generated</span>
                          <span style={{ marginLeft: "6px" }}>
                            : 16-Jan-26 06:58 PM
                          </span>
                        </div>
                        <div
                          style={{
                            margin: "3px 0px 0px 2px",
                            height: "18px",
                            width: "164px",
                            background:
                              "repeating-linear-gradient(90deg,#000 0,#000 2px,#fff 2px,#fff 4px)",
                          }}
                        />
                      </div>
                    </>
                  )}
                  {i === 2 && (
                    <>
                      <img
                        src={qrImg}
                        style={{
                          margin: "15px 0px 0px 21px",
                          width: "72px",
                          height: "66px",
                        }}
                      />
                      <div
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setDragging("col-qr");
                        }}
                        style={{
                          position: "absolute",
                          right: -6,
                          top: 0,
                          width: 10,
                          height: "100%",
                          cursor: "ew-resize",
                        }}
                      />
                      <MultipleStopIcon
                        sx={{
                          position: "absolute",
                          top: "50%",
                          right: -9,
                          fontSize: 18,
                          color: "#2a54a6",
                          pointerEvents: "none",
                        }}
                      />
                    </>
                  )}
                  {i < colWidths.length - 1 && (
                    <div
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setDragging(`col-${i}`);
                      }}
                      style={{
                        position: "absolute",
                        right: -4,
                        top: 0,
                        width: 8,
                        height: "100%",
                        cursor: "ew-resize",
                      }}
                    />
                  )}
                </div>
              ))}
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setDragging("header-resize");
                }}
                style={{
                  position: "absolute",
                  right: -8,
                  bottom: -8,
                  width: 16,
                  height: 16,
                  background: "#2a54a6",
                  borderRadius: "3px",
                  cursor: "nwse-resize",
                }}
              />
            </div>
          )}

          {/* ── CONTENT ── */}
          {viewport && (
            <div
              onMouseDown={(e) => startDrag(e, "content", contentX, contentY)}
              style={{
                position: "absolute",
                left: contentX * zoom,
                top: contentY * zoom,
                width: contentWidth * zoom,
                height: contentHeight * zoom,
                background: "rgba(185,58,207,0.07)",
                border: "2px dashed #b93acf",
                cursor: "move",
                borderRadius: "2px",
              }}
            >
              <div style={{ padding: 8 * zoom, fontSize: 10 * zoom }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr 1fr 1fr",
                    fontWeight: 700,
                    borderBottom: "1px solid #999",
                    paddingBottom: 4 * zoom,
                    marginBottom: 6 * zoom,
                  }}
                >
                  <div>INVESTIGATIONS</div>
                  <div>RESULTS</div>
                  <div>UNIT</div>
                  <div>BIO REF.</div>
                </div>
                {[
                  ["HIV", "Negative", "", "Screening"],
                  ["HBsAG", "Negative", "", "Screening"],
                  ["HCV", "Negative", "", "Screening"],
                  ["TSH", "2.31", "µIU/mL", "0.4 - 4"],
                  ["RBS", "87", "mg/dL", "75 - 140"],
                  ["Creatinine", "0.78", "mg/dL", "0.5 - 1.1"],
                ].map((row, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr 1fr",
                      padding: `${2 * zoom}px 0`,
                      borderBottom: "1px dashed #ddd",
                    }}
                  >
                    {row.map((cell, ci) => (
                      <div key={ci}>{cell}</div>
                    ))}
                  </div>
                ))}
                <div
                  style={{
                    marginTop: 6 * zoom,
                    fontStyle: "italic",
                    textAlign: "center",
                    color: "#94a3b8",
                  }}
                >
                  *** End of report ***
                </div>
              </div>
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setDragging("content-resize");
                }}
                style={{
                  position: "absolute",
                  right: -8,
                  bottom: -8,
                  width: 16,
                  height: 16,
                  background: "#b93acf",
                  borderRadius: "3px",
                  cursor: "nwse-resize",
                }}
              />
            </div>
          )}

          {/* ── TECHNICIAN SIGNATURE ── */}
          {viewport && (
            <div
              onMouseDown={(e) =>
                startDrag(e, "tech-sign", techSignX, techSignY)
              }
              style={{
                position: "absolute",
                left: techSignX,
                top: techSignY,
                width: SIGN_W,
                height: SIGN_H,
                background: "rgba(16,185,129,0.1)",
                border: "2px dashed #059669",
                cursor: "move",
                display: "flex",
                alignItems: "flex-end",
                fontSize: "8px",
                fontWeight: 600,
                borderRadius: "2px",
              }}
            >
              <div style={{ padding: "4px" }}>
                <div>lab tech</div>
                <div>line2</div>
                <div>line3</div>
                <div>line4</div>
              </div>
            </div>
          )}

          {/* ── DOCTOR SIGNATURES (1, 2, or 3) ── */}
          {viewport &&
            Array.from({ length: doctorSignCount }).map((_, idx) => (
              <div
                key={idx}
                onMouseDown={(e) =>
                  startDrag(
                    e,
                    `doc-sign-${idx}`,
                    docSignPositions[idx]?.x ?? docSignX + idx * 120,
                    docSignPositions[idx]?.y ?? docSignY,
                  )
                }
                style={{
                  position: "absolute",
                  left: docSignPositions[idx]?.x ?? docSignX + idx * 120,
                  top: docSignPositions[idx]?.y ?? docSignY,
                  width: SIGN_W,
                  height: SIGN_H,
                  background: "rgba(42,84,166,0.08)",
                  border: `2px dashed ${["#2a54a6", "#b93acf", "#f97316"][idx]}`,
                  cursor: "move",
                  display: "flex",
                  alignItems: "flex-end",
                  fontSize: "7px",
                  fontWeight: 600,
                  borderRadius: "2px",
                }}
              >
                <div style={{ padding: "4px" }}>
                  <div
                    style={{
                      fontSize: "8px",
                      fontWeight: 700,
                      color: ["#2a54a6", "#b93acf", "#f97316"][idx],
                    }}
                  >
                    Doctor {idx + 1}
                  </div>
                  <div>line2</div>
                  <div>line3</div>
                  <div>line4</div>
                </div>
                {/* Colour-coded corner indicator */}
                <div
                  style={{
                    position: "absolute",
                    top: 2,
                    right: 4,
                    fontSize: "7px",
                    fontWeight: 700,
                    color: ["#2a54a6", "#b93acf", "#f97316"][idx],
                  }}
                >
                  DR{idx + 1}
                </div>
              </div>
            ))}
        </div>
      </Grid>

      {/* ══ RIGHT PANEL ══ */}
      {viewport && (
        <Grid
          item
          xs={3}
          sx={{
            background: "#ffffff",
            borderLeft: "1px solid #e5e9ef",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": { width: "3px" },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(185,58,207,0.25)",
              borderRadius: "4px",
            },
          }}
        >
          {/* Panel header */}
          <Box
            sx={{
              px: 2.5,
              py: 1.75,
              borderBottom: "1px solid #e5e9ef",
              position: "relative",
              flexShrink: 0,
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "2px",
                background: "linear-gradient(90deg,#2a54a6,#b93acf)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  background: "linear-gradient(135deg,#2a54a6,#b93acf)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <TuneIcon sx={{ color: "#fff", fontSize: "0.95rem" }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.88rem",
                    fontWeight: 700,
                    color: "#0a1f44",
                    lineHeight: 1.2,
                  }}
                >
                  Layout Settings
                </Typography>
                <Typography
                  sx={{ fontSize: "0.62rem", color: "#94a3b8", mt: "1px" }}
                >
                  Drag boxes on canvas or edit values
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: 2, flex: 1 }}>
            {/* ── HEADER BOX ── */}
            <SectionLabel
              icon="fa-border-all"
              color="#2a54a6"
              bg="rgba(42,84,166,0.08)"
              border="rgba(42,84,166,0.2)"
              label="Header Box"
            />
            <Grid container spacing={1.25} sx={{ mb: 0.5 }}>
              {[
                { label: "X", value: headerX, onChange: (v) => setHeaderX(v) },
                { label: "Y", value: headerY, onChange: (v) => setHeaderY(v) },
                {
                  label: "Width",
                  value: headerWidth,
                  onChange: (v) => {
                    const scale = v / headerWidth;
                    setHeaderWidth(v);
                    setColWidths(colWidths.map((w) => Math.round(w * scale)));
                  },
                },
              ].map((f) => (
                <Grid item xs={4} key={f.label}>
                  <Typography sx={panelLabelStyle}>{f.label}</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    value={f.value}
                    onChange={(e) => f.onChange(+e.target.value)}
                    sx={panelFieldStyle("#2a54a6")}
                  />
                </Grid>
              ))}
            </Grid>

            <PanelDivider />

            {/* ── HEADER COLUMNS ── */}
            <SectionLabel
              icon="fa-columns"
              color="#f97316"
              bg="rgba(249,115,22,0.08)"
              border="rgba(249,115,22,0.2)"
              label="Header Columns"
            />
            <Grid container spacing={1.25} sx={{ mb: 0.5 }}>
              {[
                {
                  label: "Patient",
                  value: colWidths[0],
                  color: "rgba(42,84,166,0.12)",
                  onChange: (v) => {
                    const c = [...colWidths];
                    c[0] = v;
                    setColWidths(c);
                    setHeaderWidth(c.reduce((a, b) => a + b, 0));
                  },
                },
                {
                  label: "UHID",
                  value: colWidths[1],
                  color: "rgba(249,115,22,0.12)",
                  onChange: (v) => {
                    const c = [...colWidths];
                    c[1] = v;
                    setColWidths(c);
                    setHeaderWidth(c.reduce((a, b) => a + b, 0));
                  },
                },
                {
                  label: "QR",
                  value: colWidths[2],
                  color: "rgba(16,185,129,0.12)",
                  onChange: (v) => {
                    const c = [...colWidths];
                    c[2] = v;
                    setColWidths(c);
                    setHeaderWidth(c.reduce((a, b) => a + b, 0));
                  },
                },
              ].map((f) => (
                <Grid item xs={4} key={f.label}>
                  <Typography sx={panelLabelStyle}>{f.label}</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    value={f.value}
                    onChange={(e) => f.onChange(+e.target.value)}
                    sx={{
                      ...panelFieldStyle(),
                      "& .MuiOutlinedInput-root": {
                        ...panelFieldStyle()["& .MuiOutlinedInput-root"],
                        background: f.color,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            <PanelDivider />

            {/* ── CONTENT BOX ── */}
            <SectionLabel
              icon="fa-table"
              color="#b93acf"
              bg="rgba(185,58,207,0.08)"
              border="rgba(185,58,207,0.2)"
              label="Content Box"
            />
            <Grid container spacing={1.25} sx={{ mb: 0.5 }}>
              {[
                {
                  label: "X",
                  value: contentX,
                  onChange: (v) => setContentX(v),
                },
                {
                  label: "Y",
                  value: contentY,
                  onChange: (v) => setContentY(v),
                },
                {
                  label: "Width",
                  value: contentWidth,
                  onChange: (v) => setContentWidth(v),
                },
                {
                  label: "Height",
                  value: contentHeight,
                  onChange: (v) => setContentHeight(v),
                },
              ].map((f) => (
                <Grid item xs={6} key={f.label}>
                  <Typography sx={panelLabelStyle}>{f.label}</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    value={f.value}
                    onChange={(e) => f.onChange(+e.target.value)}
                    sx={panelFieldStyle("#b93acf")}
                  />
                </Grid>
              ))}
            </Grid>

            <PanelDivider />

            {/* ── TECHNICIAN SIGNATURE ── */}
            <SectionLabel
              icon="fa-user-cog"
              color="#059669"
              bg="rgba(16,185,129,0.08)"
              border="rgba(16,185,129,0.2)"
              label="Technician Signature"
            />
            <Grid container spacing={1.25} sx={{ mb: 0.5 }}>
              {[
                {
                  label: "X",
                  value: techSignX,
                  onChange: (v) => setTechSignX(v),
                },
                {
                  label: "Y",
                  value: techSignY,
                  onChange: (v) => setTechSignY(v),
                },
              ].map((f) => (
                <Grid item xs={6} key={f.label}>
                  <Typography sx={panelLabelStyle}>{f.label}</Typography>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    value={f.value}
                    onChange={(e) => f.onChange(+e.target.value)}
                    sx={panelFieldStyle("#059669")}
                  />
                </Grid>
              ))}
            </Grid>

            <PanelDivider />

            {/* ── DOCTOR SIGNATURES ── */}
            <SectionLabel
              icon="fa-user-md"
              color="#2a54a6"
              bg="rgba(42,84,166,0.08)"
              border="rgba(42,84,166,0.2)"
              label="Doctor Signatures"
            />

            {/* Count selector */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
            >
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: "#0a1f44",
                  flexShrink: 0,
                }}
              >
                Number of doctors:
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {[1, 2, 3].map((n) => (
                  <Box
                    key={n}
                    onClick={() => setDoctorSignCount(n)}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      border: "1.5px solid",
                      borderColor:
                        doctorSignCount === n ? "transparent" : "#e5e9ef",
                      background:
                        doctorSignCount === n
                          ? [
                              "linear-gradient(135deg,#2a54a6,#6b7fdb)",
                              "linear-gradient(135deg,#2a54a6,#b93acf)",
                              "linear-gradient(135deg,#b93acf,#f97316)",
                            ][n - 1]
                          : "#f8fafc",
                      color: doctorSignCount === n ? "#fff" : "#64748b",
                      boxShadow:
                        doctorSignCount === n
                          ? "0 3px 10px rgba(185,58,207,0.25)"
                          : "none",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "rgba(42,84,166,0.3)",
                        background:
                          doctorSignCount === n
                            ? undefined
                            : "rgba(42,84,166,0.06)",
                      },
                    }}
                  >
                    {n}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Per-doctor position fields */}
            {Array.from({ length: doctorSignCount }).map((_, idx) => {
              const colors = ["#2a54a6", "#b93acf", "#f97316"];
              const bgs = [
                "rgba(42,84,166,0.08)",
                "rgba(185,58,207,0.08)",
                "rgba(249,115,22,0.08)",
              ];
              const pos = docSignPositions[idx] ?? {
                x: docSignX + idx * 120,
                y: docSignY,
              };
              return (
                <Box key={idx} sx={{ mb: 1.25 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      mb: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "3px",
                        background: colors[idx],
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        color: "#0a1f44",
                        textTransform: "uppercase",
                        letterSpacing: ".3px",
                      }}
                    >
                      Doctor {idx + 1}
                    </Typography>
                  </Box>
                  <Grid container spacing={1.25}>
                    <Grid item xs={6}>
                      <Typography sx={panelLabelStyle}>X</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        value={pos.x}
                        onChange={(e) =>
                          setDocSignPositions((prev) => {
                            const n = [...prev];
                            n[idx] = { ...pos, x: +e.target.value };
                            return n;
                          })
                        }
                        sx={{
                          ...panelFieldStyle(),
                          "& .MuiOutlinedInput-root": {
                            ...panelFieldStyle()["& .MuiOutlinedInput-root"],
                            background: bgs[idx],
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={panelLabelStyle}>Y</Typography>
                      <TextField
                        size="small"
                        fullWidth
                        type="number"
                        value={pos.y}
                        onChange={(e) =>
                          setDocSignPositions((prev) => {
                            const n = [...prev];
                            n[idx] = { ...pos, y: +e.target.value };
                            return n;
                          })
                        }
                        sx={{
                          ...panelFieldStyle(),
                          "& .MuiOutlinedInput-root": {
                            ...panelFieldStyle()["& .MuiOutlinedInput-root"],
                            background: bgs[idx],
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              );
            })}

            <PanelDivider />

            {/* Save button */}
            <Button
              variant="contained"
              fullWidth
              onClick={saveLayout}
              startIcon={<SaveIcon sx={{ fontSize: "0.95rem !important" }} />}
              sx={{
                background: "linear-gradient(135deg, #2a54a6, #b93acf)",
                color: "#fff",
                textTransform: "none",
                fontSize: "0.85rem",
                fontWeight: 700,
                borderRadius: "10px",
                py: 1.1,
                boxShadow: "0 4px 14px rgba(185,58,207,0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg,#1e3d8a,#a02db8)",
                  boxShadow: "0 6px 20px rgba(185,58,207,0.4)",
                },
                transition: "all 0.25s ease",
              }}
            >
              Save Layout
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );
}

const ResizeHandleIcon = ({ side }) => (
  <MultipleStopIcon
    sx={{
      position: "absolute",
      top: "50%",
      [side]: -9,
      //   transform: "translateY(-50%) rotate(90deg)", // 🔑 vertical grip
      fontSize: 18,
      color: "#000",
      cursor: "ew-resize",
      //   opacity: 0.7,
      pointerEvents: "none", // visual only
      userSelect: "none",
    }}
  />
);

// Tiny section label with colour-coded pill
const SectionLabel = ({ icon, color, bg, border, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, mt: 0.5 }}>
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: "100px",
        px: 1,
        py: 0.35,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: "0.65rem",
          fontWeight: 700,
          color,
          textTransform: "uppercase",
          letterSpacing: ".5px",
        }}
      >
        {label}
      </Typography>
    </Box>
  </Box>
);

const PanelDivider = () => (
  <Box
    sx={{
      height: "1px",
      background:
        "linear-gradient(90deg,rgba(42,84,166,0.12),rgba(185,58,207,0.12),transparent)",
      my: 1.75,
    }}
  />
);

const panelLabelStyle = {
  fontSize: "0.62rem",
  fontWeight: 600,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: ".3px",
  mb: 0.4,
};

const panelFieldStyle = (accentColor = "#b93acf") => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    background: "#f8fafc",
    fontSize: "0.8rem",
    "& fieldset": { borderColor: "#e5e9ef" },
    "&:hover fieldset": { borderColor: accentColor },
    "&.Mui-focused fieldset": {
      borderColor: accentColor,
      borderWidth: "1.5px",
    },
    "& input": { py: "6px", px: "10px" },
  },
});
